import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Checkbox,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import { formatDistanceToNow, isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { UserAvatar, RegistrationPlate } from '@/components/common';
import type { Approval, ApprovalStatus, ApprovalType } from '@/types';
import { dateFnsLocale } from '@/utils/locale';

interface ApprovalCardProps {
  approval: Approval;
  onView: (approval: Approval) => void;
  onResend?: (approvalId: string) => void;
  selectable?: boolean;
  isSelected?: boolean;
  onSelect?: (approvalId: string, selected: boolean) => void;
}

const statusColors: Record<ApprovalStatus, 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  expired: 'default',
};

const urgencyColorKeys = {
  immediate: 'error.main',
  soon: 'warning.main',
  future: 'success.main',
} as const;

export function ApprovalCard({
  approval,
  onView,
  onResend,
  selectable = false,
  isSelected = false,
  onSelect,
}: ApprovalCardProps) {
  const { t } = useTranslation('approvals');
  const theme = useTheme();
  const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2)}`;

  // Resolve theme colors for type chips (need alpha for backgrounds)
  const typeColors: Record<ApprovalType, string> = {
    extra_work: theme.palette.primary.light,
    high_value: theme.palette.warning.dark,
    customer_declined: theme.palette.error.light,
    warranty_claim: theme.palette.secondary.light,
    discount_request: theme.palette.info.main,
    additional_work: theme.palette.primary.light,
    parts_replacement: theme.palette.secondary.light,
    inspection_finding: theme.palette.warning.main,
    estimate_change: theme.palette.warning.dark,
  };

  const statusLabels: Record<ApprovalStatus, string> = {
    pending: t('pending'),
    approved: t('approved'),
    rejected: t('rejected'),
    expired: t('expired'),
  };

  const typeLabels: Record<ApprovalType, string> = {
    extra_work: t('extraWork'),
    high_value: t('highValue'),
    customer_declined: t('customerDeclined'),
    warranty_claim: t('warrantyClaim'),
    discount_request: t('discountRequest'),
    additional_work: t('additionalWork'),
    parts_replacement: t('partsReplacement'),
    inspection_finding: t('inspectionFinding'),
    estimate_change: t('estimateChange'),
  };



  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onSelect?.(approval.id, e.target.checked);
  };

  const isExpiringSoon =
    approval.status === 'pending' &&
    approval.expiresAt &&
    !isPast(new Date(approval.expiresAt)) &&
    new Date(approval.expiresAt).getTime() - Date.now() < 2 * 60 * 60 * 1000; // 2 hours

  const isExpired =
    approval.status === 'pending' &&
    approval.expiresAt &&
    isPast(new Date(approval.expiresAt));

  return (
    <Card
      elevation={0}
      sx={{
        border: 1,
        borderColor: isSelected
          ? 'primary.main'
          : approval.status === 'pending'
            ? 'warning.main'
            : 'divider',
        borderLeft: 4,
        borderLeftColor:
          approval.status === 'pending'
            ? urgencyColorKeys[approval.urgency]
            : statusColors[approval.status] === 'success'
              ? 'success.main'
              : statusColors[approval.status] === 'error'
                ? 'error.main'
                : 'grey.400',
        backgroundColor: isSelected ? 'action.selected' : 'background.paper',
        overflow: 'hidden',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flex: 1 }}>
            {selectable && (
              <Checkbox
                checked={isSelected}
                onChange={handleCheckboxChange}
                onClick={(e) => { e.stopPropagation(); }}
                sx={{ p: 0, mt: -0.5 }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {approval.title}
                </Typography>
                {approval.urgency === 'immediate' && (
                  <WarningIcon sx={{ color: 'error.main', fontSize: 18, flexShrink: 0 }} />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {approval.workOrderNumber}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Chip
              size="small"
              label={typeLabels[approval.type] ?? approval.type}
              sx={{
                // An unknown type must not crash the card (#2851).
                backgroundColor: alpha(typeColors[approval.type] ?? theme.palette.primary.light, 0.08),
                color: typeColors[approval.type] ?? theme.palette.primary.light,
                fontWeight: 500,
              }}
            />
            <Chip
              size="small"
              label={isExpired ? t('expired') : statusLabels[approval.status]}
              color={isExpired ? 'default' : statusColors[approval.status]}
            />
          </Box>
        </Box>

        {/* Vehicle & Customer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsCarIcon fontSize="small" color="action" />
            <RegistrationPlate registrationNumber={approval.vehicle.registrationNumber ?? '-'} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UserAvatar
              firstName={approval.customer.firstName}
              lastName={approval.customer.lastName}
              size={24}
              variant="rounded"
              colorScheme={approval.customer.customerType === 'business' ? 'business' : 'default'}
            />
            <Typography variant="body2">
              {approval.customer.firstName} {approval.customer.lastName}
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {approval.description}
        </Typography>

        {/* Items Preview */}
        <List dense disablePadding sx={{ mb: 2 }}>
          {approval.items.slice(0, 3).map((item) => (
            <ListItem
              key={item.id}
              disablePadding
              sx={{ py: 0.25 }}
              secondaryAction={
                <Typography variant="body2" fontWeight={500}>
                  {formatPrice(item.priceCents)}
                </Typography>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">{item.name}</Typography>
                    {item.isRequired && (
                      <Chip
                        label={t('required')}
                        size="small"
                        sx={{ height: 16, fontSize: '0.6rem' }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
          {approval.items.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              {t('moreItems', { count: approval.items.length - 3 })}
            </Typography>
          )}
        </List>

        <Divider sx={{ my: 1.5 }} />

        {/* Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {formatPrice(approval.totalCents)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {approval.status === 'pending' && (
                <>
                  <AccessTimeIcon sx={{ fontSize: 14, color: isExpiringSoon ? 'error.main' : 'text.secondary' }} />
                  <Typography
                    variant="caption"
                    color={isExpiringSoon ? 'error.main' : 'text.secondary'}
                  >
                    {isExpired
                      ? t('expired')
                      : `${t('expiresIn')} ${formatDistanceToNow(new Date(approval.expiresAt!), {
                          addSuffix: true,
                          locale: dateFnsLocale(),
                        })}`}
                  </Typography>
                </>
              )}
              {approval.status !== 'pending' && approval.respondedAt && (
                <>
                  {approval.status === 'approved' ? (
                    <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                  ) : (
                    <CancelIcon sx={{ fontSize: 14, color: 'error.main' }} />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(approval.respondedAt), {
                      addSuffix: true,
                      locale: dateFnsLocale(),
                    })}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {approval.status === 'pending' && onResend && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<WhatsAppIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  onResend(approval.id);
                }}
              >
                {t('reminder')}
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={() => onView(approval)}
            >
              {t('viewApproval')}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
